// Typed fetch wrapper for the Tinsmith REST API. See specifications/api.md.
//
// - Cookie-based auth: every request sends credentials, so the `tin_session`
//   cookie rides along automatically.
// - Normalises the server's `{ error: { code, message, fields } }` envelope
//   into a thrown `ApiError` that callers can branch on by `code`.
// - Attaches an `Idempotency-Key` header to mutating requests so retries are
//   safe (honoured by the server's idempotency middleware).
// - Echoes the `tin_csrf` cookie into `X-Tinsmith-Csrf` on mutating requests
//   (auth-and-sessions.md §CSRF).

export type ApiErrorCode =
  | "conflict_state"
  | "conflict_supersede"
  | "csrf_failed"
  | "forbidden"
  | "idempotency_conflict"
  | "internal"
  | "network"
  | "not_found"
  | "payment_required"
  | "provider_rate_limited"
  | "provider_unavailable"
  | "quota_exceeded"
  | "rate_limited"
  | "re_auth_required"
  | "sandbox_unavailable"
  | "unauthenticated"
  | "unknown"
  | "validation_failed";

export class ApiError extends Error {
  readonly code: ApiErrorCode;
  readonly fields?: Record<string, unknown>;
  readonly status: number;

  constructor(
    code: ApiErrorCode,
    message: string,
    status: number,
    fields?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "ApiError";
    this.code = code;
    this.status = status;
    this.fields = fields;
  }

  get isUnauthenticated(): boolean {
    return this.code === "unauthenticated" || this.status === 401;
  }
}

/**
 * A refusal that says the task's sandbox has been released (`sandboxes.md`
 * §Nothing may be asked of a released sandbox).
 *
 * Terminal, and proof: nothing re-provisions a sandbox, so a caller that sees
 * this re-reads the task and lets the control go rather than leaving a retry
 * that cannot ever land.
 */
export const isSandboxUnavailable = (err: unknown): boolean =>
  err instanceof ApiError && err.code === "sandbox_unavailable";

// Same-origin by default; the Vite dev server proxies `/api` to the backend.
// Exported for the rare URL that is rendered rather than fetched (an `<img>`
// src, a download href) so it honours the same override as every request.
export const API_BASE =
  (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api/v1";

const MUTATING = new Set(["DELETE", "PATCH", "POST", "PUT"]);

// Double-submit CSRF token (auth-and-sessions.md §CSRF). The server mints
// `tin_csrf` alongside the session and leaves it readable — deliberately, so
// this can echo it back. A cross-origin page can make the browser send the
// cookie but cannot read it, so it cannot produce the header.
const CSRF_COOKIE = "tin_csrf";
const CSRF_HEADER = "x-tinsmith-csrf";

const csrfToken = (): string | undefined => {
  if (typeof document === "undefined") {
    return undefined;
  }
  // Last match, not first: a dogfooding preview (live-branch-previews.md §7)
  // carries both the outer deployment's `tin_csrf` and this app's own, same
  // name, because TIN_COOKIE_DOMAIN has to cover the preview host for the
  // outer one to authorize the viewer at all. Browsers order same-named
  // cookies oldest-first, so the outer one — set before this app was ever
  // opened — sorts first and this app's own sorts after it. The server's
  // proxy strips exactly the first same-named cookie out of what reaches a
  // sandbox (`PreviewProxy.ts`'s `filterCookieHeader`), so the token this app
  // actually checks against is the last one, and the header has to match.
  let token: string | undefined;
  for (const entry of document.cookie.split(";")) {
    const separator = entry.indexOf("=");
    if (separator > 0 && entry.slice(0, separator).trim() === CSRF_COOKIE) {
      token = decodeURIComponent(entry.slice(separator + 1));
    }
  }
  return token;
};

export type QueryValue = boolean | number | string | undefined;

export type RequestOptions = {
  readonly body?: unknown;
  /**
   * Content type for `rawBody`. Ignored for the JSON `body`, which is always
   * `application/json`.
   */
  readonly contentType?: string;
  readonly idempotencyKey?: string;
  readonly method?: "DELETE" | "GET" | "PATCH" | "POST" | "PUT";
  readonly query?: Readonly<Record<string, QueryValue>>;
  /**
   * A body sent as-is — the attachment upload's file bytes (`api.md` §Chat
   * attachments), not JSON. Mutually exclusive with `body`; rides the same
   * CSRF, idempotency, and re-auth machinery as everything else.
   */
  readonly rawBody?: Blob;
  readonly signal?: AbortSignal;
};

const newIdempotencyKey = (): string => {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === "function") {
    return c.randomUUID();
  }
  // Fallback for environments without WebCrypto (should not happen in the
  // browser; keeps the client usable under bare test runners).
  return `idem-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
};

const buildUrl = (
  path: string,
  query?: Readonly<Record<string, QueryValue>>,
): string => {
  const base = `${API_BASE}${path}`;
  if (!query) {
    return base;
  }
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined) {
      params.append(key, String(value));
    }
  }
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
};

const toApiError = async (response: Response): Promise<ApiError> => {
  let code: ApiErrorCode = "unknown";
  let message = `Request failed (${response.status}).`;
  let fields: Record<string, unknown> | undefined;
  try {
    const payload = (await response.json()) as {
      error?: {
        code?: string;
        fields?: Record<string, unknown>;
        message?: string;
      };
    };
    if (payload.error) {
      code = (payload.error.code as ApiErrorCode) ?? code;
      message = payload.error.message ?? message;
      fields = payload.error.fields;
    }
  } catch {
    // Non-JSON error body; keep the generic message.
  }
  return new ApiError(code, message, response.status, fields);
};

// A sensitive action can return 401 `re_auth_required` when the session is more
// than 15 minutes old (auth-and-sessions.md §Re-authentication). The app root
// registers a handler that prompts a passkey re-verification and resolves true
// once the session is refreshed; the client then transparently retries once.
let reauthHandler: (() => Promise<boolean>) | null = null;
export function setReauthHandler(
  handler: (() => Promise<boolean>) | null,
): void {
  reauthHandler = handler;
}

export async function request<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const method = options.method ?? "GET";
  // Pin the idempotency key up front so a re-auth retry reuses it (the retry is
  // a fresh attempt — the guard rejects before any side effect — but reusing the
  // key keeps us safe even if that ever changes).
  const pinned: RequestOptions =
    MUTATING.has(method) && !options.idempotencyKey
      ? { ...options, idempotencyKey: newIdempotencyKey() }
      : options;
  return requestWithReauth<T>(path, pinned, false);
}

async function requestWithReauth<T>(
  path: string,
  options: RequestOptions,
  retried: boolean,
): Promise<T> {
  try {
    return await requestOnce<T>(path, options);
  } catch (err) {
    if (
      err instanceof ApiError &&
      err.code === "re_auth_required" &&
      !retried &&
      reauthHandler
    ) {
      const verified = await reauthHandler();
      if (verified) {
        return requestWithReauth<T>(path, options, true);
      }
    }
    throw err;
  }
}

async function requestOnce<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const method = options.method ?? "GET";
  const headers: Record<string, string> = { accept: "application/json" };

  let body: Blob | string | undefined;
  if (options.rawBody !== undefined) {
    headers["content-type"] = options.contentType ?? "application/octet-stream";
    body = options.rawBody;
  } else if (options.body !== undefined) {
    headers["content-type"] = "application/json";
    body = JSON.stringify(options.body);
  }
  if (MUTATING.has(method)) {
    headers["idempotency-key"] = options.idempotencyKey ?? newIdempotencyKey();
    // Read per request rather than cached: the token is replaced on sign-in,
    // and a value captured at module load would be the previous user's.
    const csrf = csrfToken();
    if (csrf !== undefined) {
      headers[CSRF_HEADER] = csrf;
    }
  }

  let response: Response;
  try {
    response = await fetch(buildUrl(path, options.query), {
      body,
      credentials: "include",
      headers,
      method,
      signal: options.signal,
    });
  } catch (cause) {
    throw new ApiError(
      "network",
      cause instanceof Error ? cause.message : "Network request failed.",
      0,
    );
  }

  if (!response.ok) {
    throw await toApiError(response);
  }
  if (response.status === 204) {
    return undefined as T;
  }
  const text = await response.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

export const api = {
  del: <T>(path: string, options?: RequestOptions): Promise<T> =>
    request<T>(path, { ...options, method: "DELETE" }),
  get: <T>(path: string, options?: RequestOptions): Promise<T> =>
    request<T>(path, { ...options, method: "GET" }),
  patch: <T>(
    path: string,
    body?: unknown,
    options?: RequestOptions,
  ): Promise<T> => request<T>(path, { ...options, body, method: "PATCH" }),
  post: <T>(
    path: string,
    body?: unknown,
    options?: RequestOptions,
  ): Promise<T> => request<T>(path, { ...options, body, method: "POST" }),
  put: <T>(
    path: string,
    body?: unknown,
    options?: RequestOptions,
  ): Promise<T> => request<T>(path, { ...options, body, method: "PUT" }),
};
